import { Command } from 'cmdk';
import { useMemo, useCallback, useState, useRef, useEffect, useLayoutEffect } from 'react';
import type { IconName } from '@customerio/pluma-icons';
import {
  TextField,
  Box,
  ModalPrimitiveDialog,
  ModalPrimitiveOverlay,
  ModalPrimitive,
  EmptyStateDescription,
  EmptyState,
  EmptyStateTitle,
  Icon,
  Text,
  Flex,
  EmptyStateGraphic,
  Spinner,
  KeyboardShortcut,
  InlineStack,
  type ModalPrimitiveProps,
  type PlumaManagedModalProps,
  Filters,
  FiltersAddButton,
  FiltersActiveList,
  Button,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useIsAgentFullscreen } from '@/features/copilot';
import type { useCopilotSessionContext, useCopilotSidebar } from '@/features/copilot';
import { useListSourceOptions } from '@/features/pipelines/sources/hooks';
import { useListDestinationOptions } from '@/features/pipelines/destinations/hooks';
import { getServicesQueryOptions, encodeSearchFilters } from '@/services/api';
import { useQueries } from '@tanstack/react-query';
import { useDebouncedValue } from '@/hooks/state/use-debounced-value';
import type { useCurrentWorkspace } from '@/context/current-workspace';
import type { useThemeContext } from '@/context/theme';
import type { useSideNavState, useToggleSideNav } from '@customerio/pluma-components/react';
import * as styles from './index.css';
import type { DataOutResult, SearchResult, TemplateType } from '@/services/auth/types';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import {
  LABEL,
  SEARCH_RESULT_HEADINGS,
  SEARCH_KIND_FILTERS,
  COMMAND_DIALOG_QUERY_DEFAULTS,
  TEMPLATE_TYPE_FILTER_OPTIONS,
} from './constants';
import type { SearchKindFilter } from './constants';
import {
  computeItemPosition,
  buildPeopleSearchQuery,
  buildIntegrationCatalog,
  filterAndCapIntegrations,
  filterAudienceCredentials,
  filterCustomForms,
  filterFacebookLeadAdsForms,
  filterReportingWebhooks,
  filterDataOutResults,
  filterObjectTypes,
  mapAudienceCredentialToSearchResult,
  mapCustomFormToSearchResult,
  mapFacebookLeadAdsFormToSearchResult,
  mapReportingWebhookToSearchResult,
  mapCdpOutToSearchResult,
  matchesCdpOut,
  mapDataOutToSearchResult,
  mapMParticleToSearchResult,
  mapSegmentOutToSearchResult,
  mapObjectTypeToSearchResult,
  groupSearchResults,
  mapCustomerToSearchResult,
  mapPipelinesConnectionToSearchResult,
  remapUnsetTemplates,
  buildAllApiResults,
  hasAnyDeferredSource,
  DEFERRED_SOURCE_KEYS,
  scoreCustomerResult,
  type ScoredResult,
  type IntegrationCatalog,
  type ActiveSources,
} from './utils';
import { useStoredHydraCommands } from './use-stored-hydra-commands';
import { useSettingsCommands } from './use-settings-commands';
import { useLocalSearchResults } from './use-local-search-results';
import { navigationSearchOptions, type PermissionGuard } from './use-navigation-search';
import { docsSearchOptions } from './use-docs-search';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { PlumaModal as PlumaModalCss } from '@customerio/pluma-components/css/components';
import type { useAnalytics } from '@/services/analytics';
import { sentryCaptureException } from '@/utils/sentry';
import { isEmptyString } from '@/utils/string';
import { useKeyHold } from '@tanstack/react-hotkeys';
import { QuickLookPanel } from './quick-look';
import { NON_PREVIEWABLE_KINDS } from './quick-look/constants';
import { SearchResultItem } from './search-result-item';
import { LocalCommandsSection } from './local-commands-section';
import clsx from 'clsx';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';

type ActiveCategory = 'all' | 'people' | 'docs' | 'integrations' | 'forms' | 'objects' | SearchKindFilter;

const NO_SOURCES = {
  search: false,
  people: false,
  docs: false,
  navigation: false,
  integrations: false,
  adAudiences: false,
  forms: false,
  webhooks: false,
  cdpOut: false,
  dataOut: false,
  objectTypes: false,
};

export const DEFERRED_SOURCE_FALLBACK_DELAY_MS = 1500;
const CLIENT_SIDE_RESULT_CAP = 50;

const sharedQueryOptions = {
  ...COMMAND_DIALOG_QUERY_DEFAULTS,
  forwardAbortSignal: true,
} as const;

type CommandDialogData = {
  session: ReturnType<typeof useCopilotSessionContext>;
  workspace: ReturnType<typeof useCurrentWorkspace>;
  initialInputValue?: string;
  initialActiveFilter?: string;
  initialActiveSubFilter?: string | null;
  initialIsFiltersVisible?: boolean;
  initialIsQuickLookOpen?: boolean;
  persistInputValue: (value: string) => void;
  persistActiveFilter: (value: string) => void;
  persistActiveSubFilter: (value: string | null) => void;
  persistFiltersVisibility: (value: boolean) => void;
  persistQuickLookOpen: (value: boolean) => void;
  track: ReturnType<typeof useAnalytics>['track'];
  openCopilot: ReturnType<typeof useCopilotSidebar>['openCopilot'];
  colorScheme: ReturnType<typeof useThemeContext>['colorScheme'];
  setColorScheme: ReturnType<typeof useThemeContext>['setColorScheme'];
  canToggleTheme: ReturnType<typeof useThemeContext>['canToggleTheme'];
  sideNavState: ReturnType<typeof useSideNavState>;
  toggleSideNav: ReturnType<typeof useToggleSideNav>;
  isMasqWithoutAccess: boolean;
  isBuilder: boolean;
  guard: PermissionGuard | null;
};

export function CommandDialog({ data, onClose, modalState }: PlumaManagedModalProps<CommandDialogData>) {
  const { isOpen: isDialogOpen, onClose: onModalManagerClose, onCloseComplete, modalContext } = modalState;
  const isUniversalSearchMultiEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchMulti);
  const isAiEnabled = useFeatureFlag(AccountFeatureFlag.AiEnabled);
  const isAgentFullscreen = useIsAgentFullscreen();
  const showAskAssistant = isAiEnabled && !isAgentFullscreen;
  const safeNavigate = useSafeNavigate();
  const [inputValue, setInputValue] = useState(data.initialInputValue ?? '');
  const [debouncedInputValue] = useDebouncedValue(inputValue.trim(), 250);

  const isQuickLookEnabled = useFeatureFlag(AccountFeatureFlag.UniversalSearchQuickLook);
  const [selectedValue, setSelectedValue] = useState('');

  const isMetaHeld = useKeyHold('Meta');
  const isCtrlHeld = useKeyHold('Control');

  const {
    session,
    workspace,
    persistInputValue,
    persistActiveFilter,
    persistActiveSubFilter,
    persistFiltersVisibility,
    persistQuickLookOpen,
    track,
    openCopilot,
    isMasqWithoutAccess,
    isBuilder,
    guard,
  } = data;
  const workspaceId = workspace?.id ?? 0;
  const catalogWorkspaceId = workspaceId > 0 ? workspaceId : null;
  const sourceOptionsQuery = useListSourceOptions(catalogWorkspaceId);
  const destinationOptionsQuery = useListDestinationOptions(catalogWorkspaceId);

  // Each query resolves independently; the map is partial until both settle.
  // During that window, types from the slower catalog show fallback icons/names briefly.
  const catalogMap = useMemo<IntegrationCatalog>(
    () => buildIntegrationCatalog(sourceOptionsQuery.data ?? [], destinationOptionsQuery.data ?? []),
    [sourceOptionsQuery.data, destinationOptionsQuery.data],
  );

  const handleInputFocus = useCallback((event: Event & { target: HTMLInputElement }) => {
    event.target.select();
  }, []);

  const queryStartTimeRef = useRef<number | null>(null);
  const previousDebouncedInputRef = useRef(debouncedInputValue);

  const openTimeRef = useRef(Date.now());
  const previousIsDialogOpenRef = useRef(isDialogOpen);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [isFiltersVisible, setIsFiltersVisible] = useState(data.initialIsFiltersVisible ?? true);

  // used for tracking the time the user started typing to get the time to first result
  if (debouncedInputValue !== previousDebouncedInputRef.current) {
    previousDebouncedInputRef.current = debouncedInputValue;
    if (debouncedInputValue.length > 0) {
      queryStartTimeRef.current = Date.now();
    }
  }
  // used for tracking the time the dialog was opened to get the time open
  if (isDialogOpen !== previousIsDialogOpenRef.current) {
    previousIsDialogOpenRef.current = isDialogOpen;
    if (isDialogOpen === true) {
      openTimeRef.current = Date.now();
    }
  }

  const isSearchingEnabled = isDialogOpen === true && debouncedInputValue.length > 0 && workspaceId > 0;

  const canAccessForms = !isBuilder && guard?.hasPermission('journeys/imports/view') === true;
  const canAccessJourneyIntegrations =
    !isBuilder && guard?.hasAnyPermission('journeys/imports/view', 'journeys/exports/view') === true;
  // Object type discovery needs both permissions: object_types/view covers the type metadata,
  // while objects/view covers the preview row fetch and destination object list page.
  const canAccessObjectTypes =
    !isBuilder &&
    guard?.hasPermission('journeys/object_types/view') === true &&
    guard.hasPermission('journeys/objects/view') === true;

  // Deferred sources (docs, integrations, forms) fire after scroll or a keyboard accessibility fallback timeout.
  // Reset on each new query so deferred sources don't carry over to a fresh search.
  // Adjusted during render (not in an effect) to avoid the extra paint cycle.
  const [deferredEnabled, setDeferredEnabled] = useState(false);
  const [lastDebouncedInputValue, setLastDebouncedInputValue] = useState(debouncedInputValue);
  const sentinelRef = useRef<HTMLDivElement>(null);

  if (lastDebouncedInputValue !== debouncedInputValue) {
    setLastDebouncedInputValue(debouncedInputValue);
    setDeferredEnabled(false);
  }

  const [activeCategory, setActiveCategory] = useState<ActiveCategory>(() => {
    if (!isUniversalSearchMultiEnabled) return 'all';
    const stored = data.initialActiveFilter;
    if (isMasqWithoutAccess && stored === 'people') return 'all';
    if (isBuilder && (stored === SEARCH_KIND_FILTERS.campaigns || stored === SEARCH_KIND_FILTERS.actions)) return 'all';
    if (!canAccessForms && stored === 'forms') return 'all';
    if (!canAccessObjectTypes && stored === 'objects') return 'all';
    const validCategories: string[] = [
      'all',
      'people',
      'docs',
      'integrations',
      'forms',
      ...(canAccessObjectTypes ? ['objects'] : []),
      ...Object.values(SEARCH_KIND_FILTERS),
    ];
    return validCategories.includes(stored ?? '') ? (stored as ActiveCategory) : 'all';
  });

  const [activeTemplateType, setActiveTemplateType] = useState<TemplateType | null>(() => {
    if (data.initialActiveFilter !== SEARCH_KIND_FILTERS.templates) return null;
    const stored = data.initialActiveSubFilter;
    const validTemplateTypes = TEMPLATE_TYPE_FILTER_OPTIONS.map((o) => o.value as string);
    return stored != null && validTemplateTypes.includes(stored) ? (stored as TemplateType) : null;
  });

  const isPeopleFilterActive = activeCategory === 'people';
  const isDocsFilterActive = activeCategory === 'docs';
  const activeKindFilter = (Object.values(SEARCH_KIND_FILTERS) as string[]).includes(activeCategory)
    ? activeCategory
    : null;
  const hasNavigation = activeCategory === 'all' || activeCategory === 'navigation';

  const activeSources = useMemo<ActiveSources>(() => {
    // Navigation is handled locally — skip the API entirely to avoid stale Vertex results
    if (activeCategory === 'navigation') return { ...NO_SOURCES, navigation: true };
    if (isPeopleFilterActive) return { ...NO_SOURCES, people: true };
    if (isDocsFilterActive) return { ...NO_SOURCES, docs: true };
    if (activeCategory === 'integrations') return { ...NO_SOURCES, integrations: true };
    if (activeCategory === 'forms') return { ...NO_SOURCES, forms: canAccessForms };
    if (activeCategory === 'objects') return { ...NO_SOURCES, objectTypes: canAccessObjectTypes };
    if (activeKindFilter != null) return { ...NO_SOURCES, search: true };
    return {
      search: true,
      people: isUniversalSearchMultiEnabled,
      docs: isUniversalSearchMultiEnabled,
      navigation: hasNavigation,
      integrations: isUniversalSearchMultiEnabled,
      adAudiences: isUniversalSearchMultiEnabled,
      forms: isUniversalSearchMultiEnabled && canAccessForms,
      webhooks: isUniversalSearchMultiEnabled && canAccessJourneyIntegrations,
      cdpOut: isUniversalSearchMultiEnabled && canAccessJourneyIntegrations,
      dataOut: isUniversalSearchMultiEnabled && canAccessJourneyIntegrations,
      objectTypes: isUniversalSearchMultiEnabled && canAccessObjectTypes,
    };
  }, [
    activeCategory,
    isPeopleFilterActive,
    isDocsFilterActive,
    activeKindFilter,
    hasNavigation,
    isUniversalSearchMultiEnabled,
    canAccessForms,
    canAccessJourneyIntegrations,
    canAccessObjectTypes,
  ]);

  const encodedFilters = useMemo(() => {
    if (activeKindFilter != null) {
      if (activeKindFilter === SEARCH_KIND_FILTERS.templates && activeTemplateType != null) {
        return encodeSearchFilters({ kind: [activeKindFilter], template_type: [activeTemplateType] });
      }
      return encodeSearchFilters({ kind: [activeKindFilter] });
    }
    return undefined;
  }, [activeKindFilter, activeTemplateType]);

  const peopleSearchTerm = useMemo(
    () => (activeSources.people ? buildPeopleSearchQuery(debouncedInputValue) : null),
    [activeSources.people, debouncedInputValue],
  );

  const [
    searchQuery,
    docsQuery,
    peopleQuery,
    navigationQuery,
    integrationsQuery,
    adAudiencesQuery,
    formsQuery,
    facebookLeadAdsFormsQuery,
    webhooksQuery,
    cdpOutQuery,
    mixpanelQuery,
    amplitudeQuery,
    segmentOutQuery,
    mparticleQuery,
    objectTypesQuery,
  ] = useQueries({
    queries: [
      getServicesQueryOptions(
        'search',
        { params: { workspaceId }, query: { q: debouncedInputValue, filters: encodedFilters } },
        {
          enabled: isSearchingEnabled && activeSources.search,
          ...sharedQueryOptions,
          select: (data) => ({
            ...data,
            results: remapUnsetTemplates((data.results ?? []).filter((r) => r.kind !== 'navigation')),
          }),
        },
      ),
      {
        ...docsSearchOptions(debouncedInputValue),
        // Deferred in 'all' mode — fire once the sentinel scrolls into view or the fallback timeout expires.
        // In 'docs' filter mode the user has explicitly requested docs, so fire immediately.
        enabled:
          activeSources.docs &&
          isDialogOpen === true &&
          debouncedInputValue.length > 0 &&
          (activeCategory !== 'all' || deferredEnabled),
      },
      getServicesQueryOptions(
        'getPeople',
        { params: { workspaceId }, query: { email: peopleSearchTerm ?? undefined, asc: false, page: 1 } },
        {
          enabled: isSearchingEnabled && activeSources.people && peopleSearchTerm != null && !isMasqWithoutAccess,
          ...sharedQueryOptions,
          // Customers endpoint has no page size param (hardcoded 50 server-side); slice to match other sources.
          // clientScore closes over debouncedInputValue. The query key includes peopleSearchTerm
          // (derived from debouncedInputValue), so every key change triggers a new fetch and
          // select re-runs with fresh data — scores are always in sync with the current query.
          select: (data): ScoredResult[] =>
            data.customers.slice(0, 10).map((customer) => {
              const result = mapCustomerToSearchResult(customer, workspaceId);
              return { ...result, clientScore: scoreCustomerResult(result, debouncedInputValue) };
            }),
        },
      ),
      {
        ...navigationSearchOptions(debouncedInputValue, workspaceId, guard),
        enabled: isSearchingEnabled && activeSources.navigation,
      },
      getServicesQueryOptions(
        'listIntegrationsPipelines',
        { params: { environment_id: workspaceId } },
        {
          // Deferred in 'all' mode. In 'integrations' filter mode fire immediately.
          enabled: isSearchingEnabled && activeSources.integrations && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          // The endpoint returns all connections with no server-side filter or pagination.
          // Mapping is cheap; the render-side cap is applied after fuzzy-filtering in
          // `filteredIntegrations` so destinations aren't dropped on workspaces with many sources.
          select: (data) => data.connections.map(mapPipelinesConnectionToSearchResult),
        },
      ),
      getServicesQueryOptions(
        'getAudienceCredentials',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.adAudiences && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          select: (data) => data.audience_credentials,
        },
      ),
      getServicesQueryOptions(
        'getCustomForms',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.forms && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          select: (data) => data.custom_forms,
        },
      ),
      getServicesQueryOptions(
        'getFacebookLeadAdsForms',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.forms && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          select: (data) => data.facebook_lead_ads_forms,
        },
      ),
      getServicesQueryOptions(
        'getReportingWebhooks',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.webhooks && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          select: (data) => data.webhooks,
        },
      ),
      getServicesQueryOptions(
        'getCdpOut',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.cdpOut && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          // 404 means no CDP Out row — treat as empty, not an error.
          throwOnError: false,
          retry: false,
          select: (data) => data.cdp_out,
        },
      ),
      getServicesQueryOptions(
        'getDataOutMixpanel',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.dataOut && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          throwOnError: false,
          retry: false,
          select: (data) => data.integration,
        },
      ),
      getServicesQueryOptions(
        'getDataOutAmplitude',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.dataOut && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          throwOnError: false,
          retry: false,
          select: (data) => data.integration,
        },
      ),
      getServicesQueryOptions(
        'getDataOutSegmentOut',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.dataOut && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          throwOnError: false,
          retry: false,
          select: (data) => data.segment_out,
        },
      ),
      getServicesQueryOptions(
        'getDataOutMParticle',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.dataOut && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          throwOnError: false,
          retry: false,
          select: (data) => data.mparticle,
        },
      ),
      getServicesQueryOptions(
        'getObjectTypes',
        { params: { environment_id: workspaceId } },
        {
          enabled: isSearchingEnabled && activeSources.objectTypes && (activeCategory !== 'all' || deferredEnabled),
          ...sharedQueryOptions,
          select: (data) => data.object_types,
        },
      ),
    ],
  });

  const formsCompositeState = {
    isError: formsQuery.isError || facebookLeadAdsFormsQuery.isError,
    isLoading: formsQuery.isLoading || facebookLeadAdsFormsQuery.isLoading,
    isFetching: formsQuery.isFetching || facebookLeadAdsFormsQuery.isFetching,
    dataUpdatedAt: Math.max(formsQuery.dataUpdatedAt, facebookLeadAdsFormsQuery.dataUpdatedAt),
  };

  const dataOutCompositeState = {
    isError: mixpanelQuery.isError || amplitudeQuery.isError || segmentOutQuery.isError || mparticleQuery.isError,
    isLoading:
      mixpanelQuery.isLoading || amplitudeQuery.isLoading || segmentOutQuery.isLoading || mparticleQuery.isLoading,
    isFetching:
      mixpanelQuery.isFetching || amplitudeQuery.isFetching || segmentOutQuery.isFetching || mparticleQuery.isFetching,
    dataUpdatedAt: Math.max(
      mixpanelQuery.dataUpdatedAt,
      amplitudeQuery.dataUpdatedAt,
      segmentOutQuery.dataUpdatedAt,
      mparticleQuery.dataUpdatedAt,
    ),
  };

  const sources = {
    search: searchQuery,
    people: peopleQuery,
    docs: docsQuery,
    navigation: navigationQuery,
    integrations: integrationsQuery,
    adAudiences: adAudiencesQuery,
    forms: formsCompositeState,
    webhooks: webhooksQuery,
    cdpOut: cdpOutQuery,
    dataOut: dataOutCompositeState,
    objectTypes: objectTypesQuery,
  } satisfies Record<
    keyof typeof activeSources,
    { isError: boolean; isLoading: boolean; isFetching: boolean; dataUpdatedAt: number }
  >;

  const activeSourceStates = (Object.keys(activeSources) as (keyof typeof activeSources)[])
    .filter((key) => activeSources[key])
    .map((key) => sources[key]);

  // Deferred sources excluded in 'all' mode — they load after scroll/fallback and must
  // not trigger the loading spinner or clear the quick look preview for eager results.
  // In their dedicated filter modes they are the primary source and contribute normally.
  const eagerSourceStates = (Object.keys(activeSources) as (keyof typeof activeSources)[])
    .filter((key) => activeSources[key] && !(activeCategory === 'all' && DEFERRED_SOURCE_KEYS.has(key)))
    .map((key) => sources[key]);

  const isError = activeSourceStates.some((s) => s.isError);
  const isLoading = eagerSourceStates.some((s) => s.isLoading);
  const isQuickLookSourcesFetching = isSearchingEnabled && eagerSourceStates.some((s) => s.isFetching);
  const dataUpdatedAt = Math.max(0, ...activeSourceStates.map((s) => s.dataUpdatedAt));

  useEffect(() => {
    if (deferredEnabled || activeCategory !== 'all' || !isSearchingEnabled || isLoading) return;
    if (!hasAnyDeferredSource(activeSources)) return;

    const timeoutId = window.setTimeout(() => {
      setDeferredEnabled(true);
    }, DEFERRED_SOURCE_FALLBACK_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    activeCategory,
    activeSources.adAudiences,
    activeSources.docs,
    activeSources.cdpOut,
    activeSources.dataOut,
    activeSources.forms,
    activeSources.integrations,
    activeSources.objectTypes,
    activeSources.webhooks,
    debouncedInputValue,
    deferredEnabled,
    isLoading,
    isSearchingEnabled,
  ]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: 0 });
  }, [activeCategory, activeTemplateType]);

  const closeDialog = useCallback(() => {
    onClose();
  }, [onClose]);

  const filteredIntegrations = useMemo(
    () =>
      filterAndCapIntegrations(integrationsQuery.data ?? [], debouncedInputValue, catalogMap, CLIENT_SIDE_RESULT_CAP),
    [integrationsQuery.data, debouncedInputValue, catalogMap],
  );

  const filteredAdAudiences = useMemo(
    () =>
      filterAudienceCredentials(adAudiencesQuery.data ?? [], debouncedInputValue).map((c) =>
        mapAudienceCredentialToSearchResult(c, workspaceId),
      ),
    [adAudiencesQuery.data, debouncedInputValue, workspaceId],
  );

  const filteredForms = useMemo(
    () =>
      [
        ...filterCustomForms(formsQuery.data ?? [], debouncedInputValue, CLIENT_SIDE_RESULT_CAP).map((f) =>
          mapCustomFormToSearchResult(f, workspaceId),
        ),
        ...filterFacebookLeadAdsForms(
          facebookLeadAdsFormsQuery.data ?? [],
          debouncedInputValue,
          CLIENT_SIDE_RESULT_CAP,
        ).map((f) => mapFacebookLeadAdsFormToSearchResult(f, workspaceId)),
      ].slice(0, CLIENT_SIDE_RESULT_CAP),
    [formsQuery.data, facebookLeadAdsFormsQuery.data, debouncedInputValue, workspaceId],
  );

  const filteredWebhooks = useMemo(
    () =>
      filterReportingWebhooks(webhooksQuery.data ?? [], debouncedInputValue, CLIENT_SIDE_RESULT_CAP).map((w) =>
        mapReportingWebhookToSearchResult(w, workspaceId),
      ),
    [webhooksQuery.data, debouncedInputValue, workspaceId],
  );

  const cdpOutResults = useMemo(() => {
    const row = cdpOutQuery.data;
    if (row == null || !matchesCdpOut(debouncedInputValue)) return [];
    return [mapCdpOutToSearchResult(row, workspaceId)];
  }, [cdpOutQuery.data, debouncedInputValue, workspaceId]);

  const filteredObjectTypes = useMemo(
    () =>
      filterObjectTypes(objectTypesQuery.data ?? [], debouncedInputValue, CLIENT_SIDE_RESULT_CAP).map((t) =>
        mapObjectTypeToSearchResult(t, workspaceId),
      ),
    [objectTypesQuery.data, debouncedInputValue, workspaceId],
  );

  const dataOutResults = useMemo(() => {
    const results = [
      mixpanelQuery.data != null ? mapDataOutToSearchResult(mixpanelQuery.data, workspaceId) : null,
      amplitudeQuery.data != null ? mapDataOutToSearchResult(amplitudeQuery.data, workspaceId) : null,
      segmentOutQuery.data != null ? mapSegmentOutToSearchResult(segmentOutQuery.data, workspaceId) : null,
      mparticleQuery.data != null ? mapMParticleToSearchResult(mparticleQuery.data, workspaceId) : null,
    ];
    return filterDataOutResults(
      results.filter((result): result is DataOutResult => result != null),
      debouncedInputValue,
    );
  }, [
    mixpanelQuery.data,
    amplitudeQuery.data,
    segmentOutQuery.data,
    mparticleQuery.data,
    debouncedInputValue,
    workspaceId,
  ]);

  const allApiResults = useMemo(
    () =>
      buildAllApiResults(activeSources, {
        search: searchQuery.data?.results ?? [],
        people: peopleQuery.data ?? [],
        docs: docsQuery.data ?? [],
        navigation: navigationQuery.data ?? [],
        integrations: filteredIntegrations,
        adAudiences: filteredAdAudiences,
        forms: filteredForms,
        webhooks: filteredWebhooks,
        cdpOut: cdpOutResults,
        dataOut: dataOutResults,
        objectTypes: filteredObjectTypes,
      }),
    [
      activeSources,
      searchQuery.data?.results,
      peopleQuery.data,
      docsQuery.data,
      navigationQuery.data,
      filteredIntegrations,
      filteredAdAudiences,
      filteredForms,
      filteredWebhooks,
      cdpOutResults,
      dataOutResults,
      filteredObjectTypes,
    ],
  );

  // Builder filter strips campaign/action because those arrive via the shared searchQuery
  // (which can't be disabled per-kind). custom_form results are already absent for builders:
  // canAccessForms sets activeSources.forms=false, disabling the dedicated queries entirely.
  const filteredApiResults = useMemo(
    () => (isBuilder ? allApiResults.filter((r) => r.kind !== 'campaign' && r.kind !== 'action') : allApiResults),
    [allApiResults, isBuilder],
  );

  const groupedResults = useMemo(() => groupSearchResults(filteredApiResults), [filteredApiResults]);

  const resultsByKey = useMemo(() => {
    const map = new Map<string, SearchResult>();
    for (const [, items] of groupedResults) {
      for (let index = 0; index < items.length; index++) {
        const item = items[index];
        const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;
        map.set(key, item);
      }
    }
    return map;
  }, [groupedResults]);

  const selectedResult = resultsByKey.get(selectedValue) ?? null;

  const handleValueChange = useCallback((value: string) => {
    setSelectedValue(value);
  }, []);

  // Ref, not state: changes to the preference shouldn't trigger re-renders, and reading it
  // inside the effect below doesn't need to be a dependency (we only care about its value
  // when results arrive, not on every preference change).
  const preferredQuickLookOpenRef = useRef(data.initialIsQuickLookOpen ?? true);
  const [isQuickLookOpen, setIsQuickLookOpen] = useState(false);
  const isQuickLookPeekable =
    isQuickLookEnabled && selectedResult != null && !NON_PREVIEWABLE_KINDS.has(selectedResult.kind);

  // Boolean dep — effect only re-runs when results cross the empty/non-empty threshold,
  // not on every deferred-source append. Without this, deferred appends fire setIsQuickLookOpen
  // mid-keyboard-navigation and flash the preview panel.
  const hasAnyFilteredApiResults = filteredApiResults.length > 0;

  // Visibility follows persisted preference whenever a workspace search is active; syncing
  // with results also persists preference. Clearing the query or disabling search closes panel.
  // Preferred is read from preferredQuickLookOpenRef inside the effect (not a dependency). Re-sync when
  // workspace search gates, Quick Look flag, result presence, or persister identity change — not fetch flags.
  useEffect(() => {
    if (!isQuickLookEnabled) return;

    if (!isSearchingEnabled) {
      // Skip closing quick look when the dialog itself is closing — avoids animating
      // the dialog width back to its default while the close animation is in progress.
      if (isDialogOpen === true) {
        setIsQuickLookOpen(false);
      }
      return;
    }

    const preferred = preferredQuickLookOpenRef.current;
    setIsQuickLookOpen(preferred);
    if (hasAnyFilteredApiResults) {
      persistQuickLookOpen(preferred);
    }
  }, [hasAnyFilteredApiResults, isDialogOpen, isQuickLookEnabled, isSearchingEnabled, persistQuickLookOpen]);

  const setFiltersVisible = useCallback(
    (value: boolean) => {
      setIsFiltersVisible(value);
      persistFiltersVisibility(value);
      void track('universalSearch', 'filterVisibilityToggled', { Visible: value });
    },
    [persistFiltersVisibility, track],
  );

  const openQuickLook = useCallback(() => {
    if (selectedResult == null) return;
    preferredQuickLookOpenRef.current = true;
    persistQuickLookOpen(true);
    setIsQuickLookOpen(true);
    void track('universalSearch', 'previewOpened', {
      'Result Kind': selectedResult.kind,
      Query: debouncedInputValue,
    });
  }, [selectedResult, track, debouncedInputValue, persistQuickLookOpen]);

  const closeQuickLook = useCallback(() => {
    preferredQuickLookOpenRef.current = false;
    persistQuickLookOpen(false);
    setIsQuickLookOpen(false);
    void track('universalSearch', 'previewClosed', {
      'Result Kind': selectedResult?.kind ?? 'none',
      Query: debouncedInputValue,
    });
  }, [persistQuickLookOpen, track, selectedResult, debouncedInputValue]);

  const commandDialogFilters = useMemo(
    () => [
      {
        key: 'category',
        label: 'Category',
        type: 'radio' as const,
        isAlwaysShown: true,
        options: [
          { label: SEARCH_RESULT_HEADINGS.segment, value: SEARCH_KIND_FILTERS.segments },
          ...(!isBuilder
            ? [
                { label: SEARCH_RESULT_HEADINGS.campaign, value: SEARCH_KIND_FILTERS.campaigns },
                { label: SEARCH_RESULT_HEADINGS.action, value: SEARCH_KIND_FILTERS.actions },
              ]
            : []),
          { label: 'Templates', value: SEARCH_KIND_FILTERS.templates },
          { label: SEARCH_RESULT_HEADINGS.newsletter, value: SEARCH_KIND_FILTERS.newsletters },
          { label: SEARCH_RESULT_HEADINGS.goal, value: SEARCH_KIND_FILTERS.goals },
          { label: SEARCH_RESULT_HEADINGS.navigation, value: SEARCH_KIND_FILTERS.navigation },
          { label: 'Integrations', value: 'integrations' as const },
          ...(canAccessForms ? [{ label: SEARCH_RESULT_HEADINGS.custom_form, value: 'forms' as const }] : []),
          ...(canAccessObjectTypes ? [{ label: 'Objects', value: 'objects' as const }] : []),
          ...(!isMasqWithoutAccess ? [{ label: SEARCH_RESULT_HEADINGS.customer, value: 'people' as const }] : []),
          { label: SEARCH_RESULT_HEADINGS.docs, value: 'docs' as const },
        ],
      },
      ...(activeCategory === SEARCH_KIND_FILTERS.templates
        ? [
            {
              key: 'type',
              label: 'Type',
              type: 'radio' as const,
              options: [...TEMPLATE_TYPE_FILTER_OPTIONS],
            },
          ]
        : []),
    ],
    [activeCategory, isMasqWithoutAccess, isBuilder, canAccessForms, canAccessObjectTypes],
  );

  const filterValues = useMemo(() => {
    const values: { key: string; value: string }[] = [];
    if (activeCategory !== 'all') values.push({ key: 'category', value: activeCategory });
    if (activeTemplateType != null) values.push({ key: 'type', value: activeTemplateType });
    return values;
  }, [activeCategory, activeTemplateType]);

  const handleFiltersChange = useCallback(
    (key: string, value: string) => {
      void track('universalSearch', 'filterChanged', {
        'Filter Key': key,
        'Filter Value': value,
        'Previous Filter': activeCategory,
        'Previous Sub Filter': activeTemplateType ?? 'none',
        Query: debouncedInputValue,
      });
      if (key === 'category') {
        setActiveCategory(value as ActiveCategory);
        persistActiveFilter(value);
        if (value !== SEARCH_KIND_FILTERS.templates) {
          setActiveTemplateType(null);
          persistActiveSubFilter(null);
        }
      } else if (key === 'type') {
        setActiveTemplateType(value as TemplateType);
        persistActiveSubFilter(value);
      }
    },
    [persistActiveFilter, persistActiveSubFilter, track, activeCategory, activeTemplateType, debouncedInputValue],
  );

  const handleFiltersClear = useCallback(
    (key: string) => {
      void track('universalSearch', 'filterChanged', {
        'Filter Key': key,
        'Filter Value': null,
        'Previous Filter': activeCategory,
        'Previous Sub Filter': activeTemplateType ?? 'none',
        Query: debouncedInputValue,
      });
      if (key === 'type') {
        setActiveTemplateType(null);
        persistActiveSubFilter(null);
      } else {
        setActiveCategory('all');
        persistActiveFilter('all');
        setActiveTemplateType(null);
        persistActiveSubFilter(null);
      }
    },
    [persistActiveFilter, persistActiveSubFilter, track, activeCategory, activeTemplateType, debouncedInputValue],
  );

  const handleFiltersClearAll = useCallback(() => {
    void track('universalSearch', 'filterChanged', {
      'Filter Key': 'category',
      'Filter Value': 'all',
      'Previous Filter': activeCategory,
      'Previous Sub Filter': activeTemplateType ?? 'none',
      Query: debouncedInputValue,
    });
    setActiveCategory('all');
    persistActiveFilter('all');
    setActiveTemplateType(null);
    persistActiveSubFilter(null);
  }, [persistActiveFilter, persistActiveSubFilter, track, activeCategory, activeTemplateType, debouncedInputValue]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      persistInputValue(value);
    },
    [persistInputValue],
  );

  // Using inputValue instead of debouncedInputValue as we aren't calling
  // an expensive API like /search and there probably won't be many commands to filter by
  const hydraCommands = useStoredHydraCommands(inputValue);
  const settingsCommands = useSettingsCommands(inputValue, {
    colorScheme: data.colorScheme,
    setColorScheme: data.setColorScheme,
    canToggleTheme: data.canToggleTheme,
    sideNavState: data.sideNavState,
    toggleSideNav: data.toggleSideNav,
    openCopilot: data.openCopilot,
  });

  // Local search results — handlers are called on-demand with the debounced query
  const localSearchGroups = useLocalSearchResults(debouncedInputValue);

  const apiResultsCount = filteredApiResults.length;
  const localSearchCount = localSearchGroups.reduce((sum, g) => sum + g.results.length, 0);
  const quickActionsCount = hydraCommands.length + settingsCommands.length;
  const hasSettingsCommands = activeCategory === 'all' && settingsCommands.length > 0;

  const hasNoApiResults = filteredApiResults.length === 0;
  const hasApiResults = filteredApiResults.length > 0 && !isLoading;

  // useLayoutEffect guarantees both listRef.current and sentinelRef.current are set before
  // the observer is created — callback refs fire bottom-up during commit, so a callback ref
  // on the sentinel would race with listRef.current on the parent Command.List.
  // Dep is hasApiResults (not hasAnyFilteredApiResults) because the sentinel only mounts when
  // hasApiResults is true. Using hasAnyFilteredApiResults would fire the effect while isLoading
  // is still true, find sentinelRef.current null, bail — then never re-run once loading clears
  // because no dep changes at that point.
  useLayoutEffect(() => {
    if (deferredEnabled) return;
    const sentinel = sentinelRef.current;
    const list = listRef.current;
    if (sentinel == null || list == null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting === true) {
          setDeferredEnabled(true);
          observer.disconnect();
        }
      },
      { root: list, threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [deferredEnabled, hasApiResults, debouncedInputValue]);
  const hasLocalCommands = activeCategory === 'all' && hydraCommands.length > 0;
  const hasLocalSearchResults = activeCategory === 'all' && localSearchGroups.length > 0;
  const hasNoResults = hasNoApiResults && !hasSettingsCommands && !hasLocalCommands && !hasLocalSearchResults;
  /** Mirrors Command.Empty rendering "No results found for …" — quick look should show No preview, not a stale selection */
  const universalSearchShowsNoResultsEmptyState =
    debouncedInputValue.length > 0 && !isLoading && (hasNoResults || (isError && filteredApiResults.length === 0));
  const askAssistantVisible =
    showAskAssistant && debouncedInputValue.length > 0 && !isLoading && !hasNoResults && !searchQuery.isError;

  const totalVisibleCount = useMemo(() => {
    const localCount = activeCategory === 'all' ? quickActionsCount + localSearchCount : 0;
    return (askAssistantVisible ? 1 : 0) + localCount + apiResultsCount;
  }, [askAssistantVisible, activeCategory, quickActionsCount, localSearchCount, apiResultsCount]);

  const timeToFirstResultMs =
    queryStartTimeRef.current != null ? Math.max(0, dataUpdatedAt - queryStartTimeRef.current) : 0;

  const handleItemSelect = useCallback(
    (url: string, kind: SearchResult['kind'], position: number) => {
      const isExternal = kind === 'docs';
      const openInNewTab = isExternal || isMetaHeld || isCtrlHeld;

      void track('universalSearch', 'itemSelected', {
        'Item Type': 'search_result',
        'Result Kind': kind,
        'Open in New Tab': openInNewTab,
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': isFiltersVisible,
        'Preview Open': isQuickLookOpen,
      });
      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer');
        if (!isMetaHeld && !isCtrlHeld) {
          closeDialog();
        }
      } else {
        safeNavigate(url);
        closeDialog();
      }
    },
    [
      safeNavigate,
      closeDialog,
      track,
      isMetaHeld,
      isCtrlHeld,
      debouncedInputValue,
      dataUpdatedAt,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isFiltersVisible,
      isQuickLookOpen,
    ],
  );

  const handleLocalCommandSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_command',
        'Result Kind': 'command',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': isFiltersVisible,
        'Preview Open': isQuickLookOpen,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isFiltersVisible,
      isQuickLookOpen,
    ],
  );

  const handleLocalSearchSelect = useCallback(
    (onSelect: () => void | Promise<void>, position: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'local_search',
        'Result Kind': 'search',
        Query: debouncedInputValue,
        'Result Position': position,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': isFiltersVisible,
        'Preview Open': isQuickLookOpen,
      });
      void Promise.resolve(onSelect());
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isFiltersVisible,
      isQuickLookOpen,
    ],
  );

  const handleAskAssistant = useCallback(
    (resultPosition: number) => {
      void track('universalSearch', 'itemSelected', {
        'Item Type': 'ask_assistant',
        'Result Kind': 'assistant',
        Query: debouncedInputValue,
        'Result Position': resultPosition,
        'Results Count': totalVisibleCount,
        'API Results Count': apiResultsCount,
        'Open in New Tab': false,
        'Time to First Result Ms': timeToFirstResultMs,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
        'Filters Open': isFiltersVisible,
        'Preview Open': isQuickLookOpen,
      });
      openCopilot({ triggerSource: 'command_dialog' });
      if (debouncedInputValue.length > 0) {
        void session.sendMessage(debouncedInputValue).catch(sentryCaptureException);
      }
      closeDialog();
    },
    [
      closeDialog,
      track,
      debouncedInputValue,
      session,
      openCopilot,
      totalVisibleCount,
      apiResultsCount,
      timeToFirstResultMs,
      activeCategory,
      activeTemplateType,
      isFiltersVisible,
      isQuickLookOpen,
    ],
  );

  const handleDialogClose = useCallback<NonNullable<ModalPrimitiveProps['onClose']>>(
    (_event, reason) => {
      void track('universalSearch', 'closed', {
        Reason: reason ?? 'dismiss',
        'Had Query': !isEmptyString(inputValue.trim()),
        Query: inputValue.trim(),
        'Time Open Ms': Date.now() - openTimeRef.current,
        'Active Filter': activeCategory,
        'Active Sub Filter': activeTemplateType ?? 'none',
      });
      onModalManagerClose?.(_event, reason);
    },
    [track, onModalManagerClose, inputValue, activeCategory, activeTemplateType],
  );

  return (
    <ModalPrimitive
      initialFocusIndex={-1}
      animationTransitionDuration={125}
      isOpen={isDialogOpen ?? false}
      onClose={handleDialogClose}
      onCloseComplete={onCloseComplete}
      modalContext={modalContext}
    >
      <ModalPrimitiveOverlay className={PlumaModalCss.classNames.modalOverlay}>
        <ModalPrimitiveDialog
          ref={dialogRef}
          className={clsx(styles.dialog, isQuickLookOpen && isQuickLookEnabled && styles.dialogQuickLookOpen)}
        >
          <Command loop label={LABEL} shouldFilter={false} value={selectedValue} onValueChange={handleValueChange}>
            <Box mx="200" mt="100" style={{ borderBottom: `1px solid ${themeVars.color['border-minimal']}` }}>
              <InlineStack
                alignBlock="center"
                alignInline="space-between"
                gap={isUniversalSearchMultiEnabled ? '050' : undefined}
              >
                <Command.Input asChild value={inputValue} onValueChange={handleInputChange}>
                  <TextField
                    ref={inputRef}
                    autoFocus={true}
                    placeholder={LABEL}
                    onInput={(event) => handleInputChange(event.target.value)}
                    onFocus={handleInputFocus}
                    ariaLabel={
                      isBuilder
                        ? 'Use the command palette to search for templates, segments, or anything else in this workspace.'
                        : 'Use the command palette to search for campaigns, templates, segments, or anything else in this workspace.'
                    }
                    className={`${styles.largeTextField} fs-mask`}
                    icon="search"
                    iconColor="accent"
                    size="sm"
                    w="full"
                  />
                </Command.Input>
                {isQuickLookEnabled ? (
                  <Tooltip
                    content={
                      isQuickLookOpen
                        ? 'Close quick look'
                        : hasNoResults
                          ? undefined
                          : isQuickLookPeekable === false
                            ? 'Cannot preview this result'
                            : 'Open quick look'
                    }
                    placement="bottom"
                    isCustom={true}
                  >
                    {(context) => (
                      <Button
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                        variant="tertiary"
                        size="sm"
                        icon={isQuickLookOpen ? 'view-off' : 'view-on'}
                        iconPosition="trailing"
                        onClick={isQuickLookOpen ? closeQuickLook : openQuickLook}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.stopPropagation();
                            event.preventDefault();
                            if (isQuickLookOpen) {
                              closeQuickLook();
                            } else {
                              openQuickLook();
                            }
                            inputRef.current?.focus();
                          }
                        }}
                        isIconOnly={true}
                        isActive={isQuickLookOpen}
                        isDisabled={hasNoResults || (isQuickLookOpen === false && isQuickLookPeekable === false)}
                        mb="050"
                        aria-label={isQuickLookOpen ? 'Close quick look' : 'Open quick look'}
                      />
                    )}
                  </Tooltip>
                ) : null}
                {isUniversalSearchMultiEnabled ? (
                  <Tooltip
                    content={isFiltersVisible ? 'Hide filters' : 'Show filters'}
                    placement="right"
                    isCustom={true}
                  >
                    {(context) => (
                      <Button
                        ref={context.reference.setReference}
                        {...context.reference.getReferenceProps()}
                        variant="tertiary"
                        size="sm"
                        icon="filter"
                        iconPosition="trailing"
                        onClick={() => setFiltersVisible(!isFiltersVisible)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.stopPropagation();
                            event.preventDefault();
                            if (isFiltersVisible) {
                              setFiltersVisible(false);
                              inputRef.current?.focus();
                            } else {
                              setFiltersVisible(true);
                            }
                          }
                        }}
                        isIconOnly={true}
                        isActive={isFiltersVisible}
                        mb="050"
                        aria-label={isFiltersVisible ? 'Hide filters' : 'Show filters'}
                      />
                    )}
                  </Tooltip>
                ) : null}
              </InlineStack>
              {isUniversalSearchMultiEnabled ? (
                <div
                  className={clsx(styles.filtersWrapper, isFiltersVisible && styles.filtersWrapperOpen)}
                  inert={!isFiltersVisible ? true : undefined}
                >
                  {/* Stop non-Escape keys (Enter, arrows) from bubbling into Command.List, which would
                      select items or move focus out of the filters area. For Escape, we only block propagation
                      while a filter dropdown is open so Pluma can close it without also closing the modal.
                      Once the dropdown is closed, the next Escape bubbles up and closes the modal normally. */}
                  {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keydown interception explained above */}
                  <div
                    className={styles.filtersInner}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        const hasOpenDropdown = event.currentTarget.querySelector('[aria-expanded="true"]') != null;
                        if (hasOpenDropdown) {
                          event.stopPropagation();
                        }
                        return;
                      }
                      event.stopPropagation();
                    }}
                  >
                    <InlineStack alignBlock="center" pb="100">
                      <Filters
                        filters={commandDialogFilters}
                        values={filterValues}
                        onChange={handleFiltersChange}
                        onClear={handleFiltersClear}
                        onClearAll={handleFiltersClearAll}
                        addButtonVariant="subtle"
                      >
                        <InlineStack alignBlock="center" gap="100">
                          <FiltersActiveList />
                          <FiltersAddButton />
                        </InlineStack>
                      </Filters>
                    </InlineStack>
                  </div>
                </div>
              ) : null}
            </Box>
            <Flex direction="row" overflow="hidden">
              <Box flex={1} minW="0" overflow="hidden">
                <Command.List ref={listRef}>
                  {(hasSettingsCommands || hasLocalCommands) && !hasApiResults ? (
                    <LocalCommandsSection
                      settingsCommands={settingsCommands}
                      hydraCommands={hydraCommands}
                      baseOffset={0}
                      onSelect={handleLocalCommandSelect}
                    />
                  ) : null}
                  {hasLocalSearchResults ? (
                    <>
                      {localSearchGroups.map((group, groupIndex) => {
                        const groupOffset = localSearchGroups
                          .slice(0, groupIndex)
                          .reduce((sum, g) => sum + g.results.length, 0);
                        return (
                          <Command.Group key={`search-${group.heading}`} heading={group.heading}>
                            {group.results.map((result, resultIndex) => (
                              <Command.Item
                                key={`search-${result.id}`}
                                value={`search-${result.id}`}
                                onSelect={() =>
                                  handleLocalSearchSelect(
                                    result.onSelect,
                                    computeItemPosition({
                                      hydraCommandsCount: hasApiResults ? 0 : quickActionsCount,
                                      localSearchCount,
                                      section: 'localSearch',
                                      groupOffset,
                                      itemIndex: resultIndex,
                                    }),
                                  )
                                }
                                asChild
                              >
                                <Box alignItems="flex-start" gap="050" display="flex">
                                  <Icon
                                    mt="025"
                                    size="sm"
                                    sizeVersion="v2"
                                    color="base"
                                    name={(result.icon ?? 'search') as IconName}
                                  />
                                  <Flex direction="column" w="full">
                                    <Text text="product-p" truncate>
                                      {result.label}
                                    </Text>
                                    {result.description != null ? (
                                      <Text size="xxs" color="subtle" truncate>
                                        {result.description}
                                      </Text>
                                    ) : null}
                                  </Flex>
                                </Box>
                              </Command.Item>
                            ))}
                          </Command.Group>
                        );
                      })}
                    </>
                  ) : null}
                  {(hasSettingsCommands || hasLocalCommands || hasLocalSearchResults) && isLoading ? (
                    <Command.Loading asChild>
                      <EmptyState>
                        <EmptyStateGraphic>
                          <Spinner className={styles.spinner} color="information" />
                        </EmptyStateGraphic>
                        <EmptyStateTitle>Searching in {workspace?.name ?? 'this workspace'}…</EmptyStateTitle>
                      </EmptyState>
                    </Command.Loading>
                  ) : null}
                  {!hasSettingsCommands && !hasLocalCommands && !hasLocalSearchResults && isLoading ? (
                    <Command.Loading asChild>
                      <EmptyState>
                        <EmptyStateGraphic>
                          <Spinner className={styles.spinner} color="information" />
                        </EmptyStateGraphic>
                        <EmptyStateTitle>
                          Searching for "<span className="fs-mask">{debouncedInputValue}</span>"
                        </EmptyStateTitle>
                        <EmptyStateDescription>
                          Hang on while we search in {workspace?.name ?? 'this workspace'}
                        </EmptyStateDescription>
                      </EmptyState>
                    </Command.Loading>
                  ) : null}
                  {debouncedInputValue.length === 0 && !hasLocalCommands && !hasLocalSearchResults ? (
                    <Command.Empty asChild>
                      <EmptyState>
                        <EmptyStateTitle icon="search">Search {workspace?.name ?? 'Workspace'}</EmptyStateTitle>
                        <EmptyStateDescription>
                          {isBuilder
                            ? 'Try searching for templates, segments, or anything else in this workspace.'
                            : 'Try searching for campaigns, templates, segments, or anything else in this workspace.'}
                        </EmptyStateDescription>
                      </EmptyState>
                    </Command.Empty>
                  ) : null}
                  {universalSearchShowsNoResultsEmptyState ? (
                    <Command.Empty asChild>
                      <EmptyState>
                        <EmptyStateTitle icon="search">
                          No results found for "<span className="fs-mask">{debouncedInputValue}</span>"
                        </EmptyStateTitle>
                        <EmptyStateDescription>Try adjusting your search to find matches.</EmptyStateDescription>
                      </EmptyState>
                    </Command.Empty>
                  ) : null}
                  {hasApiResults ? (
                    <>
                      {groupedResults.map(([kind, items], groupIndex) => {
                        const groupOffset = groupedResults
                          .slice(0, groupIndex)
                          .reduce((sum, [, groupItems]) => sum + groupItems.length, 0);
                        return (
                          <Command.Group
                            key={kind}
                            heading={SEARCH_RESULT_HEADINGS?.[kind] ?? `Search Results for '${kind}'`}
                          >
                            {items.map((item, index) => {
                              const key = `${item.kind}-${index}-${item.doc_id ?? encodeURIComponent(item.url)}`;
                              return (
                                <SearchResultItem
                                  key={key}
                                  item={item}
                                  itemKey={key}
                                  position={computeItemPosition({
                                    hydraCommandsCount: 0,
                                    localSearchCount: activeCategory === 'all' ? localSearchCount : 0,
                                    section: 'apiResults',
                                    groupOffset,
                                    itemIndex: index,
                                  })}
                                  onSelect={handleItemSelect}
                                  catalog={catalogMap}
                                />
                              );
                            })}
                          </Command.Group>
                        );
                      })}
                    </>
                  ) : null}
                  {(hasSettingsCommands || hasLocalCommands) && hasApiResults ? (
                    <LocalCommandsSection
                      settingsCommands={settingsCommands}
                      hydraCommands={hydraCommands}
                      baseOffset={localSearchCount + apiResultsCount}
                      onSelect={handleLocalCommandSelect}
                    />
                  ) : null}
                  {askAssistantVisible ? (
                    <Command.Item
                      key="ask-assistant"
                      value="ask-assistant"
                      onSelect={() =>
                        handleAskAssistant(
                          (activeCategory === 'all' ? quickActionsCount + localSearchCount : 0) + apiResultsCount + 1,
                        )
                      }
                      asChild
                    >
                      <Box alignItems="flex-start" gap="050" display="flex">
                        <Icon mt="025" size="sm" sizeVersion="v2" color="base" name="sparkle" />
                        <Flex direction="column" w="full">
                          <Text text="product-p" truncate w="full">
                            Ask Agent
                          </Text>
                          <Text size="xxs" color="subtle" truncate w="full">
                            Send &ldquo;<span className="fs-mask">{debouncedInputValue}</span>&rdquo; to the AI Agent
                          </Text>
                        </Flex>
                      </Box>
                    </Command.Item>
                  ) : null}
                  {hasApiResults ? <div ref={sentinelRef} aria-hidden="true" style={{ height: 0 }} /> : null}
                </Command.List>
              </Box>
              <div
                className={clsx(
                  styles.quickLookContainer,
                  (isQuickLookOpen === false || isQuickLookEnabled === false) && styles.quickLookContainerHidden,
                )}
              >
                {isQuickLookOpen && isQuickLookEnabled ? (
                  <QuickLookPanel
                    isLoading={isQuickLookSourcesFetching && !universalSearchShowsNoResultsEmptyState}
                    result={universalSearchShowsNoResultsEmptyState ? null : selectedResult}
                    integrationCatalog={catalogMap}
                  />
                ) : null}
              </div>
            </Flex>
          </Command>
          <InlineStack
            w="full"
            alignInline="space-between"
            py="100"
            px="200"
            alignBlock="center"
            className={styles.footer}
          >
            <Flex gap="050" alignItems="center">
              <KeyboardShortcut hotkey="Escape" />
              <Text size="xxs" color="subtle">
                Close
              </Text>
            </Flex>

            <InlineStack alignInline="space-between" gap="300">
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="ArrowUp" />
                <KeyboardShortcut hotkey="ArrowDown" />
                <Text size="xxs" color="subtle">
                  Navigate
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Enter" />
                <Text size="xxs" color="subtle">
                  Select
                </Text>
              </Flex>
              <Flex gap="050" alignItems="center">
                <KeyboardShortcut hotkey="Mod+Enter" />
                <Text size="xxs" color="subtle">
                  Open in new tab
                </Text>
              </Flex>
            </InlineStack>
          </InlineStack>
        </ModalPrimitiveDialog>
      </ModalPrimitiveOverlay>
    </ModalPrimitive>
  );
}
